import React from 'react';
import packageJson from '../package.json';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  deferredPrompt?: any;
  onInstallComplete?: () => void;
}

const VERSION = `v${packageJson.version}`;

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, deferredPrompt, onInstallComplete }) => {
  const [showAllUpdates, setShowAllUpdates] = React.useState(false);
  
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b-4 border-indigo-600 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h2 className="text-3xl font-black text-indigo-600">⚙️ 設定</h2>
            <span className="text-sm font-bold text-slate-500 bg-slate-100 px-3 py-1 rounded-full">{VERSION}</span>
          </div>
          <button 
            onClick={onClose}
            className="text-3xl text-slate-400 hover:text-slate-600 transition-colors"
          >
            ✕
          </button>
        </div>
        
        <div className="p-6 space-y-6 text-slate-800">
          {/* 更新紀錄區塊 */}
          <section className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
            <h3 className="text-2xl font-black text-indigo-600 mb-3">📝 更新紀錄</h3>
            <div className="space-y-3">
              {/* 最新兩個版本 - 始終顯示 */}
              <div className="border-l-4 border-indigo-500 pl-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-black text-indigo-600">v1.9.0</span>
                  <span className="text-xs text-slate-500">2026-01</span>
                </div>
                <p className="text-sm text-slate-700">下載功能優化：修復 Android LINE 瀏覽器下載問題，新增瀏覽器檢測與提示功能，引導用戶使用 Google Chrome 或其他瀏覽器以確保正常儲存圖片。優化圖片壓縮處理，避免大圖片造成黑屏問題。字體更新：將圓體字體更換為「Huninn 粉圓體」，支援完整繁體中文字符，避免台灣地區缺字問題。</p>
              </div>
              <div className="border-l-4 border-slate-300 pl-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-bold text-slate-600">v1.8.0</span>
                  <span className="text-xs text-slate-500">2026-01</span>
                </div>
                <p className="text-sm text-slate-600">表情符號支援：新增完整 Emoji 支援功能，使用 Twitter Emoji 確保跨平台一致顯示。優化空格間距處理，修正半形空格顯示問題。</p>
              </div>
              
              {/* 歷史版本 - 可折疊 */}
              {showAllUpdates && (
                <>
                  <div className="border-l-4 border-slate-300 pl-4">
                    <div className="mb-1">
                      <span className="font-bold text-slate-600">v1.6.0</span>
                    </div>
                    <p className="text-sm text-slate-600">字體更新：將「楷體」改為「手寫體」，使用 Google Fonts 的「ZCOOL KuaiLe（站酷快樂體）」，提供溫暖的手寫風格效果。預設字體改為明體。</p>
                  </div>
                  <div className="border-l-4 border-slate-300 pl-4">
                    <div className="mb-1">
                      <span className="font-bold text-slate-600">v1.5.0</span>
                    </div>
                    <p className="text-sm text-slate-600">顏色選擇優化：祝賀語顏色與日期顏色完全分開，日期顏色支援彩虹漸層。優化楷體字體顯示，使用更穩定的字體方案。</p>
                  </div>
                  <div className="border-l-4 border-slate-300 pl-4">
                    <div className="mb-1">
                      <span className="font-bold text-slate-600">v1.4.0</span>
                    </div>
                    <p className="text-sm text-slate-600">修復日期進階設定邏輯：套用整體顏色時不再重置其他進階設定。優化楷體字體支援繁體中文。新增「加入主畫面」功能按鈕。</p>
                  </div>
                  <div className="border-l-4 border-slate-300 pl-4">
                    <div className="mb-1">
                      <span className="font-bold text-slate-600">v1.3.0</span>
                    </div>
                    <p className="text-sm text-slate-600">字體功能重大更新：新增手機版書法風格字體支援（楷體、圓體），優化字體載入與回退機制，確保所有字體在手機上正常顯示。</p>
                  </div>
                  <div className="border-l-4 border-slate-300 pl-4">
                    <div className="mb-1">
                      <span className="font-bold text-slate-600">v1.2.0</span>
                    </div>
                    <p className="text-sm text-slate-600">新增說明頁面、更新紀錄、手機色彩選擇修正、進階日期設定區塊、檔案下載優化與 SEO/OG 標籤。</p>
                  </div>
                  <div className="border-l-4 border-slate-300 pl-4">
                    <div className="mb-1">
                      <span className="font-bold text-slate-600">v1.1.x</span>
                    </div>
                    <p className="text-sm text-slate-600">調整版面配置與文字樣式。</p>
                  </div>
                  <div className="border-l-4 border-slate-300 pl-4">
                    <div className="mb-1">
                      <span className="font-bold text-slate-600">v1.0.0</span>
                    </div>
                    <p className="text-sm text-slate-600">初版早安圖製作功能上線。</p>
                  </div>
                </>
              )}
              
              {/* 展開/收起按鈕 */}
              <button
                onClick={() => setShowAllUpdates(!showAllUpdates)}
                className="w-full py-2 text-sm text-indigo-600 font-bold hover:bg-indigo-50 rounded-lg transition-colors"
              >
                {showAllUpdates ? '▲ 收起歷史版本' : '▼ 展開歷史版本'}
              </button>
            </div>
          </section>

          {/* 加入主畫面功能 */}
          <section className="bg-indigo-50 border-2 border-indigo-200 rounded-2xl p-4">
            <h3 className="text-xl font-black text-indigo-700 mb-3">📱 加入主畫面</h3>
            <p className="text-sm text-indigo-800 mb-3">將早安圖製作器加入手機主畫面，使用更方便！</p>
            <button
              onClick={async () => {
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                const isAndroid = /Android/.test(navigator.userAgent);
                const isStandalone = (window.navigator as any).standalone || window.matchMedia('(display-mode: standalone)').matches;
                
                if (isStandalone) {
                  alert('✅ 已經加入主畫面了！');
                  return;
                }
                
                if (isIOS) {
                  // iOS Safari 無法直接觸發安裝，只能顯示提示
                  alert('📱 iOS 加入主畫面步驟：\n1. 點擊 Safari 底部的「分享」按鈕\n2. 選擇「加入主畫面」\n3. 點擊「加入」即可');
                } else if (deferredPrompt) {
                  // 有 deferredPrompt，可以直接觸發安裝提示
                  try {
                    deferredPrompt.prompt();
                    const { outcome } = await deferredPrompt.userChoice;
                    if (outcome === 'accepted') {
                      console.log('用戶接受了安裝提示');
                    } else {
                      console.log('用戶拒絕了安裝提示');
                    }
                    // 清除 deferredPrompt，因為它只能使用一次
                    if (onInstallComplete) {
                      onInstallComplete();
                    }
                  } catch (err) {
                    console.error('觸發安裝提示時發生錯誤:', err);
                    alert('無法觸發安裝提示，請手動在瀏覽器選單中選擇「安裝應用程式」或「加入主畫面」。');
                  }
                } else if (isAndroid) {
                  // Android 但沒有 deferredPrompt，顯示手動步驟
                  alert('📱 Android 加入主畫面步驟：\n1. 點擊瀏覽器選單（三個點）\n2. 選擇「加入主畫面」或「安裝應用程式」\n3. 確認安裝即可');
                } else {
                  // 桌面版或其他瀏覽器
                  alert('此功能主要支援手機版。\n\n在手機瀏覽器中打開此網站，即可看到加入主畫面的選項。');
                }
              }}
              className="w-full bg-indigo-600 text-white py-4 rounded-xl text-lg font-black shadow-lg active:scale-95 transition-transform"
            >
              📱 加入主畫面
            </button>
          </section>

          {/* 未來設定功能區塊（預留空間） */}
          <section className="bg-indigo-50 border-2 border-indigo-200 rounded-2xl p-4">
            <h3 className="text-xl font-black text-indigo-700 mb-2">🔧 進階設定</h3>
            <p className="text-sm text-indigo-800 mb-3">更多自訂功能即將推出...</p>
            <div className="space-y-2 text-sm text-indigo-700">
              <div className="flex items-center gap-2 opacity-60">
                <span>☐</span>
                <span>自訂祝賀詞列表（開發中）</span>
              </div>
              <div className="flex items-center gap-2 opacity-60">
                <span>☐</span>
                <span>更多個人化設定（開發中）</span>
              </div>
            </div>
          </section>

          {/* 關於與聯絡 */}
          <section className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
            <h3 className="text-xl font-black text-indigo-600 mb-3">ℹ️ 關於</h3>
            <div className="space-y-3 text-slate-700">
              <div>
                <p className="font-bold mb-1">早安圖製作器 MGPICMAKER</p>
                <p className="text-sm">免費線上早安圖製作工具，所有處理都在本地完成，保護您的隱私。</p>
              </div>
              <div className="border-t border-slate-200 pt-3">
                <p className="text-sm font-bold mb-2">開發者：BB007</p>
                <div className="flex flex-col gap-2 text-sm">
                  <a 
                    href="mailto:suipeei@gmail.com" 
                    className="text-indigo-600 underline decoration-indigo-200 hover:decoration-indigo-400 transition-colors"
                  >
                    📧 Mail me
                  </a>
                  <a 
                    href="https://ko-fi.com/bb007" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-indigo-600 underline decoration-indigo-200 hover:decoration-indigo-400 transition-colors"
                  >
                    ☕ Coffee to me
                  </a>
                </div>
              </div>
              <div className="border-t border-slate-200 pt-3 text-xs text-slate-500">
                <p>© 2026 BB007</p>
              </div>
            </div>
          </section>
        </div>

        <div className="sticky bottom-0 bg-slate-50 border-t-2 border-slate-200 px-6 py-4">
          <button 
            onClick={onClose}
            className="w-full bg-indigo-600 text-white py-4 rounded-xl text-xl font-black shadow-lg active:scale-95 transition-transform"
          >
            關閉
          </button>
        </div>
      </div>
    </div>
  );
};
